import * as React from 'react';
import { toast } from 'sonner';

import { api } from '../../lib/api-client';
import { useAuthStore } from '../../stores/auth-store';

import type { AppNotification, NotificationPage } from './types';

interface UseNotificationsResult {
  notifications: AppNotification[];
  unreadCount: number;
  hasMore: boolean;
  filter: 'all' | 'unread';
  setFilter: (filter: 'all' | 'unread') => void;
  loadMore: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const PAGE_SIZE = 20;

function normalizeApiBase(url: string): string {
  const trimmed = url.replace(/\/+$/, '');
  return trimmed.endsWith('/v1') ? trimmed : `${trimmed}/v1`;
}

function streamUrl(token: string): string {
  const base = normalizeApiBase(import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost:3001');
  const url = new URL(`${base}/notifications/stream`);
  url.searchParams.set('token', token);
  return url.toString();
}

export function useNotifications(panelOpen: boolean, onToastClick: () => void): UseNotificationsResult {
  const token = useAuthStore((state) => state.token);
  const [notifications, setNotifications] = React.useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [cursor, setCursor] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<'all' | 'unread'>('all');
  const panelOpenRef = React.useRef(panelOpen);
  const reconnectRef = React.useRef(1_000);
  const retryTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    panelOpenRef.current = panelOpen;
  }, [panelOpen]);

  const loadFirstPage = React.useCallback(async () => {
    const response = await api.get<NotificationPage>('/notifications', {
      params: { unread: filter === 'unread', limit: PAGE_SIZE },
    });
    setNotifications(response.data.items);
    setCursor(response.data.nextCursor);
  }, [filter]);

  React.useEffect(() => {
    if (!token) return;
    void loadFirstPage();
    void api.get<{ count: number }>('/notifications/unread-count').then((response) => {
      setUnreadCount(response.data.count);
    });
  }, [loadFirstPage, token]);

  React.useEffect(() => {
    if (!token || typeof EventSource === 'undefined') return undefined;

    let closed = false;
    let source: EventSource | null = null;

    const connect = (): void => {
      source = new EventSource(streamUrl(token));
      source.addEventListener('open', () => {
        reconnectRef.current = 1_000;
      });
      source.addEventListener('notification', (event) => {
        const notification = JSON.parse(event.data) as AppNotification;
        setNotifications((current) => [notification, ...current.filter((item) => item.id !== notification.id)].slice(0, 100));
        setUnreadCount((count) => count + 1);
        if (!panelOpenRef.current) {
          toast(notification.title, {
            description: notification.message,
            duration: notification.type === 'CAMPAIGN_FAILED' || notification.type === 'PAYMENT_FAILED' ? Infinity : 3_000,
            action: { label: 'Open', onClick: onToastClick },
          });
        }
      });
      source.onerror = () => {
        source?.close();
        if (closed) return;
        const delay = reconnectRef.current;
        reconnectRef.current = Math.min(delay * 2, 30_000);
        retryTimerRef.current = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closed = true;
      source?.close();
      if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
    };
  }, [onToastClick, token]);

  async function loadMore(): Promise<void> {
    if (!cursor) return;
    const response = await api.get<NotificationPage>('/notifications', {
      params: { unread: filter === 'unread', limit: PAGE_SIZE, cursor },
    });
    setNotifications((current) => [...current, ...response.data.items]);
    setCursor(response.data.nextCursor);
  }

  async function markRead(id: string): Promise<void> {
    const existing = notifications.find((item) => item.id === id);
    await api.patch<AppNotification>(`/notifications/${id}/read`);
    setNotifications((current) =>
      current.map((item) => (item.id === id ? { ...item, read: true, readAt: new Date().toISOString() } : item)),
    );
    if (existing && !existing.read) setUnreadCount((count) => Math.max(0, count - 1));
  }

  async function markAllRead(): Promise<void> {
    await api.patch('/notifications/read-all');
    setNotifications((current) =>
      current.map((item) => ({ ...item, read: true, readAt: item.readAt ?? new Date().toISOString() })),
    );
    setUnreadCount(0);
  }

  return {
    notifications,
    unreadCount,
    hasMore: Boolean(cursor),
    filter,
    setFilter,
    loadMore,
    markRead,
    markAllRead,
  };
}
